/**
 * View component for /groups/landing/:groupId/:groupSlug
 *
 * Parses the URL params, fetches group, and renders the correct group landing page template based on the group's template setting.
 * Passes all params to the template to be passed to the iframe.
 */

// import primary libraries
import React, { useEffect, useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'

// import global components
import WaitOn from '../../../global/components/helpers/WaitOn'
import ErrorCard from '../../../global/components/helpers/ErrorCard.jsx'
import DefaultLayout from '../../../global/components/layouts/DefaultLayout.jsx'
import Spinner from '../../../global/components/helpers/Spinner.jsx'
import EmailInput from '../../../global/components/forms/EmailInput.jsx'

// utils
import { usePageTitle, useURLSearchParams } from '../../../global/utils/customHooks.js'
import { isSafeUrl } from '../../../global/utils/linkUtils.js'

// import services
import { useGetGroup } from '../groupService'
import { publicGroupLandingPageEndpoint } from '../groupStore';
import { myGroupUsersEndpoint } from '../../groupUser/groupUserStore.js';
import { useGetGroupUser } from '../../groupUser/groupUserService.js';
import { useLoggedInUser } from '../../user/authService.js'

// import resource components
import GroupLayout from '../components/GroupLayout.jsx'
import FullScreenModule from '../../contentModule/components/FullScreenModule.jsx'

import PublicLandingTemplate1 from '../components/PublicLandingTemplate1.jsx'
import PublicLandingTemplate2 from '../components/PublicLandingTemplate2.jsx'
import PublicLandingTemplate3 from '../components/PublicLandingTemplate3.jsx'
import BrukinsaTemplate2 from '../components/BrukinsaTemplate2.jsx'
import PublicLandingDefaultTemplate from '../components/PublicLandingDefaultTemplate.jsx'
import UserLoginFlow from '../../user/components/UserLoginFlow.jsx'
import UserSignupFlow from '../../user/components/UserSignupFlow.jsx'
import apiUtils from '../../../global/utils/api.js'

// Define a default template as a fallback
const DefaultTemplate = PublicLandingDefaultTemplate; // or another generic fallback template

// Select the template based on the group's settings
const RenderTemplate = (({ template, ...props }) => {
  switch (template) {
    case 'template-1':
      return <PublicLandingTemplate1 {...props} />
    case 'template-2':
      return <PublicLandingTemplate2 {...props} />
    case 'template-3':
      return <PublicLandingTemplate3 {...props} />
    case 'brukinsa':
      return <BrukinsaTemplate2 {...props} />
    case 'default':
      return <DefaultTemplate {...props} />
    default:
      return <DefaultTemplate {...props} />
  }
});

const learnUrl = window.learnUrl
const buildIframeUrl = ({ contentModuleId, groupId, searchParams = {} }) => {
  if(!contentModuleId || !groupId) return null;
  const url = new URL(`${learnUrl}/anonymous/content-modules/${contentModuleId}/overview`);
  url.searchParams.append('groupId', groupId);
  url.searchParams.append('embed', 'true');
  // add all the search params to the iframe url so we can capture them on the UserModule in Athens
  for (const [key, value] of Object.entries(searchParams)) {
    if(value) {
      url.searchParams.append(key, value);
    }
  }
  return url.href;
}

const GroupLandingTemplateWrapper = () => {
  const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);
  const [signupModalIsOpen, setSignupModalIsOpen] = useState(false);
  const [emailInputModalIsOpen, setEmailInputModalIsOpen] = useState(false);
  const [inputEmail, setInputEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  // so we can tell if the user is logged in already and change button text
  const loggedInUser = useLoggedInUser();

  // the slug is in the url for the user's convenience, but we only need the id
  const { groupId, groupSlug } = useParams();

  // see if loggedInUser is a part of the group to determine if we show dashboard or join group button
  let loggedInListEndpoint = myGroupUsersEndpoint
  const { data: groupUser } = useGetGroupUser(loggedInListEndpoint, {_user: loggedInUser?._id, _group: groupId, status: 'active'});
  const isGroupMember = !!groupUser;

  // strip the raw email so we can replace the space with a +
  const [{ email: rawEmail, autoLogin, ...params }, setParams] = useURLSearchParams({});
  // we want to preserve the + here. only replace the first occurance. if there are two +'s, it will remain invalid.
  const email = rawEmail?.replace(' ', '+')
  const searchParams = { ...params };
  // add back the parsed email
  if(email) searchParams.email = email;

  // get the group and its modules
  const endpoint = publicGroupLandingPageEndpoint(groupId);
  // pass all the search params to the query so we can capture them in UserEvent logs
  const { data: group, otherData: { contentModules } = {}, ...groupQuery } = useGetGroup(endpoint, { ...searchParams });

  usePageTitle(`${group?.publicLandingPage?.customPageTitle || group?.name || 'Group'} Landing Page`);
  // grab template string to be passed separately to the RenderTemplate component
  // if required email is true, we'll show the email input modal that will append the email to the search params
  const { template, requireEmail, logoUrl1 } = group?.publicLandingPage || {};

  // const requireEmail = true // testing

  useEffect(() => {
    if(requireEmail && !email) {
      setEmailInputModalIsOpen(true);
    }
  }, [requireEmail, email]);

  // so we can automatically open this based on url params
  useEffect(() => {
    if(autoLogin) {
      setLoginModalIsOpen(true);
    }
  }, [autoLogin]);

  // build the iframe url for the selected module
  const iframeUrl = selectedModuleId && buildIframeUrl({ contentModuleId: selectedModuleId, groupId, searchParams });

  // build link to send the user to on login
  const loginRedirect = isSafeUrl(searchParams.redirect) || '/';
  // build a link back to here in case the user resets their password. Include all the search params so they can be re-applied and add autoLogin so the login modal opens automatically.
  const originRedirect = window.location.origin + window.location.pathname + `?${apiUtils.queryStringFromObject(searchParams)}&autoLogin=true`;
  return (
    <div
      className={`bg-pf-white h-full ${selectedModuleId || loginModalIsOpen || emailInputModalIsOpen ? 'overflow-hidden h-screen' : ''}`}
    >
      <WaitOn query={groupQuery}
        fallback={<Skeleton />}
        empty={(
          <section className={`relative max-w-screen-2xl mx-auto bg-inherit w-screen h-full flex rounded-sm`}>
            <ErrorCard title='Apologies,' message={`We could not load this page. ${loggedInUser?.admin ? `\n\nHint: 'Group not found'` : ''}`} />
          </section>
        )}
        error={(serverErrorMessage) => {
          return (
            <section className={`relative max-w-screen-2xl mx-auto bg-inherit w-screen h-full flex rounded-sm`}>
              <ErrorCard title='Apologies,' message={`We could not load this page. ${loggedInUser?.admin ? `\n\nHint: ${serverErrorMessage}` : ''}`} />
            </section>
          )
        }}
      >
        <section className={`relative max-w-screen-2xl bg-inherit w-screen h-full rounded-sm mx-auto px-4 sm:px-20 ${groupQuery?.isFetching ? 'opacity-50' : ''}`}>
          <RenderTemplate
            template={template}
            // probably makes sense to pass all the unknowable url params as a single object to the template
            searchParams={searchParams}
            templateConfig={group?.publicLandingPage}
            contentModules={contentModules}
            handleOpenModule={(id) => setSelectedModuleId(id)}
            handleLoginClick={() => setLoginModalIsOpen(true)}
            handleSignupClick={() => setSignupModalIsOpen(true)}
            isLoggedIn={!!loggedInUser}
            groupRegistrationAccess={group?.userRegistration?.hasAccess}
            isGroupMember={isGroupMember}
          />
        </section>
      </WaitOn>
      <FullScreenModule
        src={selectedModuleId && iframeUrl}
        handleClose={() => setSelectedModuleId(null)}
      />
      {emailInputModalIsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-auto py-10">
          <div className="bg-neutral-pri p-8 rounded-lg shadow my-auto mx-auto min-w-fit h-fit sm:w-[600px] transition-all mt-[10vh] sm:mt-[15vh] border border-neutral-pri">
            <div className="flex flex-col gap-8">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setParams({ email: inputEmail });
                  setEmailInputModalIsOpen(false);
                }}
                className='flex flex-col gap-8'
              >
                <h1 className="text-5xl m-0">Welcome!</h1>
                <div className='flex flex-col gap-6'>
                  <div className='flex flex-col gap-2'>
                    <p>Enter a valid email to view site</p>
                    <EmailInput
                      autoFocus={true}
                      name="inputEmail"
                      value={inputEmail}
                      change={(e) => setInputEmail(e.target.value)}
                      onValidationChange={setIsEmailValid}
                    />
                  </div>
                  <button
                    className="btn w-full flex items-center justify-center"
                    disabled={!inputEmail || !isEmailValid}
                  >
                    Continue to Site
                    <i className='fa fa-arrow-right ml-2' />
                  </button>
                </div>
                {/* TODO: figure out where we want to link or whatever we are going to do here */}
                {/* <div className='flex flex-row gap-2 px-6 text-info-700'>
                  <a className='text-inherit' href='#'>Terms and Conditions</a>
                  <i className='fa fa-question-circle text-lg text-inherit' />
                </div> */}
              </form>
            </div>
          </div>
        </div>
      )}
      {loginModalIsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-neutral-pri p-4 shadow-md my-auto mx-auto mt-0 w-full h-full sm:mt-[5vh] sm:h-[75vh] sm:min-w-[600px] sm:w-fit overflow-auto rounded">
            <div className="flex">
              <button className="ml-auto flex btn-xs btn-second p-2" onClick={() => setLoginModalIsOpen(false)}><i className="fa fa-times w-4 h-4 text-base text-inherit" /></button>
            </div>
            <UserLoginFlow
              wrapperClass='min-h-max w-full md:max-w-lg mx-auto'
              // not sure where we want to redirect on login, but we can pass whatever we want here.
              // NOTE: If we want to redirect to this same page, we'll need to give the user a link on the page to somewhere other than here or they will be stuck.
              loginRedirect={loginRedirect}
              logoUrl={logoUrl1}
              // this is used to tack on to the forgot password link as a redirect so the user will eventually make it back here to login instead of being redirected to the standard login page.
              // in general though, we could pass any login protected route here and the user would be redirected there after password reset and standard login.
              originRedirect={originRedirect}
              welcomeMessage='Sign in to view all content and resources via Simulations Plus.'
            />
          </div>
        </div>
      )}
      {signupModalIsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-neutral-pri p-4 shadow-md my-auto mx-auto mt-0 w-full h-full sm:mt-[5vh] sm:h-[75vh] sm:min-w-[600px] sm:w-fit overflow-auto rounded">
            <div className="flex bg-neutral-pri">
              <button className="ml-auto flex btn-xs btn-second p-2" onClick={() => setSignupModalIsOpen(false)}><i className="fa fa-times w-4 h-4 text-base text-inherit" /></button>
            </div>
            <UserSignupFlow
              wrapperClass='min-h-max max-w-lg min-w-fit mx-auto'
              logoUrl={logoUrl1}
              welcomeMessage='Register a new account to view all content and resources via Simulations Plus.'
              handleClose={() => setSignupModalIsOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const Skeleton = () => {
  return (
    <section className={`w-screen bg-neutral-pri h-screen flex items-center justify-center mx-auto animate-pulse`}>
      <Spinner />
    </section>
  )
}

GroupLandingTemplateWrapper.Skeleton = GroupLayout.Skeleton;

export default GroupLandingTemplateWrapper

